import { memo, useEffect, useState } from 'react';
import { isPortalI18nActive, portalLanguage } from '../i18n';
import { useTenant } from './TenantContext';
import {
  isPresetStylesheetLoaded,
  loadPresetScript,
  loadPresetStylesheet,
  normalizeThemePreset,
  presetAssetUrl,
} from '../util/themePreset';

/**
 * The marketing hero's live scene, on the booking portal.
 *
 * WHAT IT IS. aperiomobility.com's hero is the mascot in front of a city
 * skyline with an "Aperio Cloud · Live" card beside him: eight vehicles and
 * eight cities on a 5.2s cycle, packets running up the dotted streams, and a
 * Pause control under it. A visitor handed over to this portal used to lose
 * all of it. This component brings the real thing across.
 *
 * THE SCENE IS NOT REIMPLEMENTED HERE, and must not be. `public/aperio/
 * scene.js` and `scene-data.js` are byte-identical copies of the site's own
 * files and `public/aperio/live-scene.css` is its own CSS with the preset
 * scope added; `scripts/check-aperio-assets.js` fails `npm test` when any of
 * them drifts. All this file owns is:
 *
 *   1. the DOM skeleton the site's scene.js looks for - `.hero-art > .live >
 *      #skyline / .ground / .streams / <picture> / .cloud`, and the
 *      `#liveToggle` button under it - with `apr-*` hooks beside the site's
 *      class names so the portal can place it on its own page;
 *   2. `data-hero="live"` on <html> while it is mounted, which is the switch
 *      the site's own rules are written against;
 *   3. loading those three files, once, and tearing the scene down again when
 *      the visitor leaves the step.
 *
 * It renders NOTHING for any tenant that is not on the Aperio preset, so Go
 * Oil's booking page is untouched - no markup, no stylesheet, no script.
 */

const STYLESHEET = 'aperio/live-scene.css';
const SCENE_DATA = 'aperio/scene-data.js';
const SCENE = 'aperio/scene.js';

const MASCOT_SIZES = '(max-width:900px) 100vw, 560px';

/** Same key the marketing hand-off writes (hooks/usePrefilledAddress.ts). */
const LANG_KEY = 'bookingLang';

/**
 * The three strings the scene's markup carries, in the three languages the
 * marketing site ships. They are the built site's own words: the card header
 * and the control's labels are static text on the site, translated by its
 * build (`aperio/site/dist/{fr,es}/index.html`), so they are copied here
 * rather than re-translated. Anything else is English, as the portal is.
 */
type SceneCopy = { head: string; pause: string; play: string; paused: string };
const COPY: Record<string, SceneCopy> = {
  en: {
    head: 'Aperio Cloud · Live', pause: 'Pause', play: 'Play', paused: 'Paused',
  },
  fr: {
    head: 'Aperio · En direct',
    pause: 'Pause',
    play: 'Lecture',
    paused: 'En pause',
  },
  es: {
    head: 'Aperio · En vivo',
    pause: 'Pausa',
    play: 'Reproducir',
    paused: 'En pausa',
  },
};

/**
 * The language the visitor is reading in, from the same two places the
 * hand-off hook takes it from, plus whatever is already on <html>. Read in
 * that order and without depending on any other effect having run: a child's
 * effects run BEFORE its parent's, so the scene can be mounting while
 * usePrefilledAddress has yet to write `document.documentElement.lang`.
 */
function readLang(): string {
  // The portal's own language when it runs translations (Aperio preset).
  if (isPortalI18nActive()) return portalLanguage();
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(LANG_KEY);
  } catch (e) {
    // private mode - the URL and <html lang> are enough
  }
  const lang = (fromUrl || stored || document.documentElement.lang || 'en')
    .slice(0, 2)
    .toLowerCase();
  return COPY[lang] ? lang : 'en';
}

/**
 * On desktop the mascot is the step's LCP element (the form card is text on
 * paper), so the 960px copy is asked for at high priority as soon as the step
 * renders. On a phone the form comes first and stays the LCP, and the art is
 * below the fold - nothing is preloaded there.
 */
function preloadMascot(): void {
  const wide = window.matchMedia
    && window.matchMedia('(min-width: 901px)').matches;
  const href = presetAssetUrl('aperio/mascot-scene-960.webp');
  if (!wide || document.querySelector(`link[data-scene-preload="${href}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = href;
  link.type = 'image/webp';
  link.setAttribute('fetchpriority', 'high');
  link.setAttribute('data-scene-preload', href);
  document.head.appendChild(link);
}

function AperioLiveScene() {
  const preset = normalizeThemePreset(useTenant()?.themePreset);
  const active = preset === 'aperio';
  // Read once, at first render: scene.js replaces the card header's text node
  // with a span of its own, so this text must never be re-rendered under it.
  const [lang] = useState(readLang);
  const copy = COPY[lang];
  // The art is not rendered until live-scene.css is in. Rendered unstyled it
  // sat full width under the form and then jumped into the right-hand column
  // when the sheet arrived, pulling the footer with it - the step's layout
  // shift of up to 1.19 (QA 2026-09-25 #36). public/index.html starts the
  // sheet with the page on Aperio's hosts, so it is normally already here.
  const [styled, setStyled] = useState(
    () => isPresetStylesheetLoaded(STYLESHEET),
  );

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    preloadMascot();
    if (styled) {
      return undefined;
    }
    let cancelled = false;
    loadPresetStylesheet(STYLESHEET).then(() => {
      if (!cancelled) setStyled(true);
    });
    return () => {
      cancelled = true;
    };
  }, [active, styled]);

  useEffect(() => {
    if (!active || !styled) {
      return undefined;
    }

    const root = document.documentElement;
    const hadHero = root.getAttribute('data-hero');
    root.setAttribute('data-hero', 'live');

    // scene.js reads <html lang> at boot to pick its French/Spanish dictionary
    // for the telemetry rows, and window.t for the one string it writes itself
    // ("Paused"), which on the site comes from site.js. Neither exists in the
    // portal, so both are seeded here from the same hand-off the page was
    // opened with - before the scene is loaded, never after.
    // With the portal's own translations, <html lang> is already the
    // language on screen (fr-CA / es, which scene.js reads like the site's).
    if (!isPortalI18nActive() && root.lang !== lang) {
      root.lang = lang;
    }
    const w = window as unknown as {
      t?: (s: string) => string;
      APERIO_SCENE?: { mount?: () => boolean };
      AperioScene?: { destroy?: () => void } | null;
    };
    if (typeof w.t !== 'function') {
      w.t = (s: string) => (s === 'Paused' ? copy.paused : s);
    }

    let cancelled = false;
    const teardown = () => {
      if (w.AperioScene && w.AperioScene.destroy) {
        w.AperioScene.destroy();
      }
    };

    (async () => {
      // The stylesheet is in (see `styled`): the scene's first vehicle renders
      // synchronously as soon as scene.js is evaluated, and it should not
      // paint unstyled.
      if (w.APERIO_SCENE && w.APERIO_SCENE.mount) {
        // Second visit to this step in the same session: the files are already
        // in the document, so the scene is re-mounted onto the new nodes.
        w.APERIO_SCENE.mount();
      } else {
        await loadPresetScript(SCENE_DATA);
        // scene.js mounts itself against the DOM above as it is evaluated.
        await loadPresetScript(SCENE);
      }
      if (cancelled) {
        teardown();
      }
    })();

    return () => {
      cancelled = true;
      teardown();
      if (hadHero === null) {
        root.removeAttribute('data-hero');
      } else {
        root.setAttribute('data-hero', hadHero);
      }
    };
  }, [active, styled, lang, copy.paused]);

  if (!active || !styled) {
    return null;
  }

  const mascot = (name: string) => presetAssetUrl(`aperio/${name}`);
  const webp = `${mascot('mascot-scene-640.webp')} 640w, `
    + `${mascot('mascot-scene-960.webp')} 960w, `
    + `${mascot('mascot-scene.webp')} 1280w`;
  const png = `${mascot('mascot-scene-640.png')} 640w, `
    + `${mascot('mascot-scene-960.png')} 960w, `
    + `${mascot('mascot-scene.png')} 1280w`;

  return (
    // .apr-art is the portal's slot (the site's hero grid does not exist
    // here); .apr-art-box/.hero-art and everything inside it is the site's own
    // hero markup, aperio/site/src/pages/home.html.
    <div className="apr-art">
      <div className="apr-art-box hero-art">
        <div className="apr-live live" aria-hidden="true">
          <div className="sky" id="skyline" />
          <div className="ground" />
          <div className="streams">
            <svg
              viewBox="0 0 600 600"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path className="ds" d="M 112 486 C 96 440 84 400 108 322" />
              <path className="ds b" d="M 104 490 C 88 444 76 404 100 326" />
              <path className="ds c" d="M 120 482 C 104 436 92 396 116 318" />
              <circle className="pk" r="3" />
              <circle className="pk two" r="2.5" />
              <circle className="pk three" r="2" />
            </svg>
          </div>
          <picture>
            <source
              type="image/webp"
              srcSet={webp}
              sizes={MASCOT_SIZES}
            />
            <img
              className="mascot-fg"
              src={mascot('mascot-scene.png')}
              srcSet={png}
              sizes={MASCOT_SIZES}
              width="1280"
              height="720"
              alt=""
              decoding="async"
              // `fetchpriority` is a DOM attribute React 17's types do not
              // know; it is what tells the browser this image, and not the
              // stepper's icons, is the one the page is waiting on.
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...({ fetchpriority: 'high' } as Record<string, string>)}
            />
          </picture>
          <div className="cloud">
            <div className="ch">
              <svg
                width="16"
                height="12"
                viewBox="0 0 24 16"
                fill="currentColor"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M19 8h-.6a6 6 0 00-11.6 1A4 4 0 007 16h12a4 4 0 000-8z"
                />
              </svg>
              {/* A bare text node, as on the site: scene.js finds it and
                  swaps it for "· Paused" while the visitor has the scene
                  stopped. Wrapping it in an element would silently break
                  that. */}
              {copy.head}
              <i />
            </div>
            <div className="city" id="cityLbl">TORONTO · ONTARIO</div>
            <div className="cbody" id="cloudBody" />
          </div>
        </div>
        {/* The one real control, outside the aria-hidden scene. scene.js only
            reveals it (.on) once something is actually moving. */}
        <div className="kl-foot live-foot">
          <button
            type="button"
            className="kl-toggle"
            id="liveToggle"
            aria-pressed="false"
          >
            <span className="kl-lab kl-lab-pause">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                focusable="false"
              >
                <rect x="6" y="4" width="4" height="16" rx="1.2" />
                <rect x="14" y="4" width="4" height="16" rx="1.2" />
              </svg>
              {copy.pause}
            </span>
            <span className="kl-lab kl-lab-play">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M7 4.5l12 7.5-12 7.5z" />
              </svg>
              {copy.play}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(AperioLiveScene);
